import * as yup from "yup";
import { salesRoles } from "../../assets/helpers/roles";
import {
  PaymentStatus,
  userType,
  enumStatus,
  locksmithPayMethods,
  LOCKSMITH_MAX_PRICE,
} from "../helpers/options";
import {
  filterOptionById,
  isCraneVehicleClass,
} from "../helpers/funciones";

// File that contains the validation schemas for all forms

const passwordRegex =
  /^(?:$|(?=.*[A-Z])(?=.*[0-9].*[0-9])(?=.*[!@#$%^&*()\-_+={}|;':",<.>/?`~]).{8,})$/;

const numberRegex = /^[0-9]*\.?[0-9]+$/;

export const LoginSchema = (t) => {
  return yup.object().shape({
    email: yup
      .string()
      .required("Obligatorio")
      .email(t("schema.invalid", { label: t("form.labels.email") })),
    password: yup
      .string()
      .min(
        3,
        t("schema.min_length", {
          label: t("form.labels.password"),
          characters: 3,
        })
      )
      .max(
        50,
        t("schema.max_length", {
          label: t("form.labels.password"),
          characters: 50,
        })
      )
      .required("Obligatorio"),
  });
};

export const LoginOTPSchema = () => {
  return yup.object().shape({
    otp: yup.string().required("Obligatorio"),
  });
};

export const UpdateTrackingSchema = (t) => {
  return yup.object().shape({
    type: yup.string().required("Obligatorio"),
    message: yup.string().required("Obligatorio"),
    note: yup.string(),
  });
};

export const filterCasesSchema = (t) => {
  return yup.object().shape({
    sinceDate: yup.string().required("Obligatorio"),
    untilDate: yup.string().required("Obligatorio"),
  });
};

export const filterExportableSchema = (t) => {
  return yup.object().shape({
    sinceDate: yup.string().required("Obligatorio"),
    untilDate: yup.string().required("Obligatorio"),
  });
};

export const CancelServiceSchema = (t) => {
  return yup.object().shape({
    cancellationReason: yup.string().required("Obligatorio"),
    cancellationDescription: yup.string().required("Obligatorio"),
  });
};

export const CancelCaseSchema = (t) => {
  return yup.object().shape({
    cancelationReason: yup.string().required("Obligatorio"),
    cancelationReasonDescription: yup.string().required("Obligatorio"),
  });
};

export const ActivateDriverSchema = (t) => {
  return yup.object().shape({
    vehicle: yup.string().optional(""),
  });
};

export const FailedCaseSchema = (t) => {
  return yup.object().shape({
    failedReason: yup.string().required("Obligatorio"),
  });
};

export const ChangeOperatorSchema = (t) => {
  return yup.object().shape({
    user: yup.string().required("Obligatorio"),
  });
};

export const FinishServiceSchema = (status) => {
  if (
    [
      enumStatus?.FOLLOW_UP,
      enumStatus?.CUSTOMER_ON_ROAD,
      enumStatus?.PENDING_LOAD,
    ]?.includes(status)
  ) {
    return yup.object().shape({
      providerFailureReason: yup.string().optional(""),
      providerFailureDescription: yup.string().optional(""),
    });
  } else {
    return yup.object().shape({
      providerFailureReason: yup.string().required("Obligatorio"),
      providerFailureDescription: yup.string().required("Obligatorio"),
    });
  }
};

export const CreateProviderSchema = (t) => {
  return yup.object().shape({
    companyName: yup.string().required("Obligatorio"),
    contactName: yup.string().required("Obligatorio"),
    identification: yup.string().required("Obligatorio"),
    identificationType: yup.string().required("Obligatorio"),
    phonePrefix: yup.string().required("Obligatorio"),
    phoneNumber: yup
      .string()
      .min(7, "Ingrese un telefono valido")
      .max(7, "Ingrese un telefono valido")
      .required("Obligatorio"),
    type: yup.string().required("Obligatorio"),
    state: yup.string().required("Obligatorio"),
    city: yup.string().required("Obligatorio"),
    isInternal: yup.boolean().required("Obligatorio"),
    isActive: yup.boolean().required("Obligatorio"),
  });
};

export const CreateVehicularAssetSchema = (t, vehicleClassesOptions = []) => {
  return yup.object().shape({
    class: yup.string().required("Obligatorio"),
    brand: yup.string().required("Obligatorio"),
    model: yup.string().required("Obligatorio"),
    plate: yup.string().required("Obligatorio"),
    color: yup.string().required("Obligatorio"),
    alias: yup.string().required("Obligatorio"),
    year: yup.string().required("Obligatorio"),
    purchaseDate: yup.string().required("Obligatorio"),
    description: yup.string(),
    type: yup.string().when("class", {
      is: (classId) =>
        isCraneVehicleClass(filterOptionById(vehicleClassesOptions, classId)),
      then: yup.string().required("Obligatorio"),
      otherwise: yup.string(),
    }),
  });
};

export const CreateIncidenceSchema = (t) => {
  return yup.object().shape({
    originLocationStatus: yup.string().required("Obligatorio"),

    isOriginBasement: yup.boolean().required("Obligatorio"),
    basementLevel: yup.string().when("isOriginBasement", {
      is: true,
      then: yup.string().required("Obligatorio"),
    }),
    originState: yup.string().required("Obligatorio"),
    originCity: yup.string().required("Obligatorio"),
    // originAddress: yup.string().required('Obligatorio'),
    // For ally cases
    businessAlly: yup.string().optional(""),
    firstName: yup.string().when("businessAlly", {
      is: (value) => value !== undefined && value !== "",
      then: yup.string().required("Obligatorio"),
    }),
    lastName: yup.string().when("businessAlly", {
      is: (value) => value !== undefined && value !== "",
      then: yup.string().required("Obligatorio"),
    }),
    phone: yup.string().when("businessAlly", {
      is: (value) => value !== undefined && value !== "",
      then: yup.string().required("Obligatorio"),
    }),
    vehicleBrand: yup.string().when("businessAlly", {
      is: (value) => value !== undefined && value !== "",
      then: yup.string().required("Obligatorio"),
    }),
    vehicleModel: yup.string().when("businessAlly", {
      is: (value) => value !== undefined && value !== "",
      then: yup.string().required("Obligatorio"),
    }),
    vehicleColor: yup.string().when("businessAlly", {
      is: (value) => value !== undefined && value !== "",
      then: yup.string().required("Obligatorio"),
    }),
    vehicleYear: yup.string().when("businessAlly", {
      is: (value) => value !== undefined && value !== "",
      then: yup.string().required("Obligatorio"),
    }),
    vehiclePlate: yup.string().when("businessAlly", {
      is: (value) => value !== undefined && value !== "",
      then: yup.string().required("Obligatorio"),
    }),

    contactPhone: yup.string().required("Obligatorio"),
    customerFailureReason: yup.string().when("segViaje", {
      is: false,
      then: yup.string().required("Obligatorio"),
    }),
    originReferencePoint: yup.string(),
    customerFailureDescription: yup.string(),
    withOutDestinyAddress: yup.boolean(),
    segViaje: yup.boolean(),
    thirdPartyService: yup.boolean(),
    // Destiny Location Schema
    destinyLocationStatus: yup.string().when("withOutDestinyAddress", {
      is: false,
      then: yup.string().required("Obligatorio"),
    }),
    destinyState: yup.string().when("withOutDestinyAddress", {
      is: false,
      then: yup.string().required("Obligatorio"),
    }),
    destinyCity: yup.string().when("withOutDestinyAddress", {
      is: false,
      then: yup.string().required("Obligatorio"),
    }),
    destinyAddress: yup.string().when("withOutDestinyAddress", {
      is: false,
      then: yup.string().required("Obligatorio"),
    }),
    caseKm: yup.string().when("withOutDestinyAddress", {
      is: false,
      then: yup.string().required("Debe calcular la distancia"),
    }),
    estimatedTime: yup.string().when("withOutDestinyAddress", {
      is: false,
      then: yup.string(),
    }),
    // Third party service schema
    thirdPartyParticularCustomerName: yup.string().when("thirdPartyService", {
      is: true,
      then: yup.string().required("Obligatorio"),
    }),
    thirdPartyParticularCustomerLastName: yup
      .string()
      .when("thirdPartyService", {
        is: true,
        then: yup.string().required("Obligatorio"),
      }),
    thirdPartyServicePhonePrefix: yup.string().when("thirdPartyService", {
      is: true,
      then: yup.string().required("Obligatorio"),
    }),
    thirdPartyServicePhoneNumber: yup.string().when("thirdPartyService", {
      is: true,
      then: yup
        .string()
        .required("Obligatorio")
        .min(7, "Ingrese un telefono valido")
        .max(7, "Ingrese un telefono valido"),
    }),
    thirdPartyVehicleBrand: yup.string().when("thirdPartyService", {
      is: true,
      then: yup.string().required("Obligatorio"),
    }),
    thirdPartyVehicleModel: yup.string().when("thirdPartyService", {
      is: true,
      then: yup.string().required("Obligatorio"),
    }),
    thirdPartyVehicleColor: yup.string().when("thirdPartyService", {
      is: true,
      then: yup.string().required("Obligatorio"),
    }),
    thirdPartyVehicleYear: yup.string().when("thirdPartyService", {
      is: true,
      then: yup.string().required("Obligatorio"),
    }),
    thirdPartyVehiclePlate: yup.string().when("thirdPartyService", {
      is: true,
      then: yup.string().required("Obligatorio"),
    }),
  });
};

export const conciliateSchema = (totalAmount, dataType) => {
  return yup.object().shape({
    paymentDate: yup.string("").required(""),
  });
};

export const conciliateSummarySchema = (totalAmount, dataType) => {
  return yup.object().shape({
    reconciledDate: yup.string("").required(""),
  });
};

export const providerPaymentSchema = (totalAmount, dataType) => {
  return yup.object().shape({
    billNumber: yup.string("").optional(""),
    providerMethods: yup
      .number("")
      .min(1, "Ingrese una cantidad valida de metodos de pago")
      .required("Obligatorio"),
    method1: yup.string().required("Obligatorio"),
    amount1: yup
      .string()
      .required("Obligatorio")
      .when(["providerMethods", "billNumber"], {
        is: (providerMethods, billNumber) => providerMethods > 0,
        then: yup
          .string()
          .test(
            "is-equal-to-totalAmount",
            "La suma de los montos debe ser igual al total",
            function (value) {
              const { parent } = this;
              const adjustedTotalAmount = parent.billNumber
                ? (totalAmount * 1.16).toFixed(2)
                : totalAmount.toFixed(2);
              if (parent.providerMethods > 1) {
                const amount2 = parseFloat(parent.amount2 || 0);
                return (
                  parseFloat(value) + amount2 ===
                  parseFloat(adjustedTotalAmount)
                );
              }
              return parseFloat(value) === parseFloat(adjustedTotalAmount);
            }
          ),
      }),
    amountBsS1: yup.string().required("Obligatorio"),

    method2: yup.string().when("providerMethods", {
      is: (providerMethods) => providerMethods > 1,
      then: yup
        .string("")
        .min(0, "Ingrese un monto valido")
        .required("Obligatorio"),
    }),
    amount2: yup.string().when(["providerMethods", "billNumber"], {
      is: (providerMethods, billNumber) => providerMethods > 1,
      then: yup
        .string()
        .required("Obligatorio")
        .test(
          "is-equal-to-totalAmount",
          "La suma de los montos debe ser igual al total",

          function (value) {
            const { parent } = this;
            const adjustedTotalAmount = parent.billNumber
              ? (totalAmount * 1.16).toFixed(2)
              : totalAmount.toFixed(2);
            const amount1 = parseFloat(parent.amount1 || 0);
            return (
              parseFloat(value) + amount1 === parseFloat(adjustedTotalAmount)
            );
          }
        ),
    }),
    amountBsS2: yup.string().when("providerMethods", {
      is: (providerMethods) => providerMethods > 1,
      then: yup
        .string("")
        .min(0, "Ingrese un monto valido")
        .required("Obligatorio"),
    }),
    observation: yup.string().optional("Obligatorio"),
    providerPaidDate: yup.string().optional(),
    ExchangeRate: yup.string().optional(),
  });
};

export const CreateUsersSchema = () => {
  return yup.object().shape({
    firstName: yup.string().required("Obligatorio"),
    lastName: yup.string().required("Obligatorio"),
    identification: yup.string().required("Obligatorio"),
    email: yup.string().email().required("Obligatorio"),
    password: yup
      .string()
      .trim()
      .matches(passwordRegex, "Contraseña en formato invalido")
      .required("Obligatorio"),
    confirmPassword: yup
      .string()
      .matches(passwordRegex, "Contraseña en formato invalido")
      .oneOf(
        [yup.ref("password"), null],
        "Contraseña  y confirmación deben coincidir"
      )
      .required(""),
    role: yup.string().required("Obligatorio"),
    shift: yup.string().required("Obligatorio"),
    module: yup.string().required("Obligatorio"),
    status: yup.string().required("Obligatorio"),
    type: yup.string().when("role", {
      is: salesRoles?.SALES_COUNTER,
      then: yup.string().required("Obligatorio"),
    }),
  });
};

export const EditUsersSchema = () => {
  return yup.object().shape({
    firstName: yup.string().optional(""),
    lastName: yup.string().optional(""),
    identification: yup.string().optional(""),
    email: yup.string().email().optional(""),
    password: yup
      .string()
      .trim()
      .matches(passwordRegex, "Contraseña en formato invalido"),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref("password"), null],
        "Contraseña  y confirmación deben coincidir"
      ),
    status: yup.string().optional(""),
  });
};

// Cerrajeria: costo opcional (max 30$) y datos de la factura del cliente
const emptyToUndefined = (value, originalValue) =>
  originalValue === "" || originalValue === null ? undefined : value;

const isLocksmithInvoice = (isLocksmith, withInvoice) =>
  isLocksmith === true && withInvoice === true;

const requiredForLocksmithMethod = (method, schema) =>
  yup.string().when(["isLocksmith", "withInvoice", "locksmithPayMethod"], {
    is: (isLocksmith, withInvoice, payMethod) =>
      isLocksmithInvoice(isLocksmith, withInvoice) && payMethod === method,
    then: schema,
  });

const servicePriceField = yup.number().when("isLocksmith", {
  is: true,
  then: yup
    .number()
    .transform(emptyToUndefined)
    .typeError("Ingrese un monto valido")
    .moreThan(0, "Ingrese un monto valido")
    .max(LOCKSMITH_MAX_PRICE, `El costo máximo es ${LOCKSMITH_MAX_PRICE}$`)
    .optional(),
  otherwise: yup.number().required("Obligatorio"),
});

const locksmithPaymentFields = {
  isLocksmith: yup.boolean(),
  withInvoice: yup.boolean(),
  hasLocksmithInvoiceFile: yup.boolean(),
  locksmithInvoice: yup
    .mixed()
    .when(["isLocksmith", "withInvoice", "hasLocksmithInvoiceFile"], {
      is: (isLocksmith, withInvoice, hasFile) =>
        isLocksmithInvoice(isLocksmith, withInvoice) && !hasFile,
      then: yup.mixed().required("Obligatorio"),
    }),
  locksmithPayMethod: yup.string().when(["isLocksmith", "withInvoice"], {
    is: isLocksmithInvoice,
    then: yup.string().required("Obligatorio"),
  }),
  locksmithBank: yup.string().when(["isLocksmith", "withInvoice"], {
    is: isLocksmithInvoice,
    then: yup.string().required("Obligatorio"),
  }),
  locksmithPhone: requiredForLocksmithMethod(
    locksmithPayMethods.PAGO_MOVIL,
    yup
      .string()
      .required("Obligatorio")
      .matches(/^04\d{2}-?\d{7}$/, "Formato 04XX-XXXXXXX")
  ),
  locksmithIdType: requiredForLocksmithMethod(
    locksmithPayMethods.PAGO_MOVIL,
    yup.string().required("Obligatorio")
  ),
  locksmithId: requiredForLocksmithMethod(
    locksmithPayMethods.PAGO_MOVIL,
    yup
      .string()
      .required("Obligatorio")
      .matches(/^\d{5,10}$/, "Ingrese solo números")
  ),
  locksmithAccount: requiredForLocksmithMethod(
    locksmithPayMethods.TRANSFERENCIA,
    yup
      .string()
      .required("Obligatorio")
      .matches(/^\d{4}-?\d{4}-?\d{2}-?\d{10}$/, "Debe tener 20 dígitos")
  ),
  locksmithFullName: requiredForLocksmithMethod(
    locksmithPayMethods.TRANSFERENCIA,
    yup.string().trim().required("Obligatorio")
  ),
};

export const CreateAVFormSchema = (t) => {
  return yup.object().shape({
    ...locksmithPaymentFields,
    paymentStatus: yup.string().required("Obligatorio"),
    contact: yup.string(),
    vehicleYear: yup.string(),
    vehicleBrand: yup.string(),
    vehicleModel: yup.string(),
    vehiclePlate: yup.string(),
    vehicleColor: yup.string(),
    estimatedTime: yup.string(),
    price: servicePriceField,
    authorizedBy: yup.string().when("paymentStatus", {
      is: (paymentStatus) => [PaymentStatus?.COURTESY]?.includes(paymentStatus),
      then: yup.string("").required("Obligatorio"),
    }),
    pvp: yup.number().when("paymentStatus", {
      is: (paymentStatus) =>
        [
          PaymentStatus?.DE_CONTADO,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup
        .number("")
        .min(0, "Ingrese un monto valido")
        .required("Obligatorio"),
    }),
    paymentMethods: yup.number().when(["pvp", "paymentStatus"], {
      is: (pvp, paymentStatus) =>
        pvp > 0 &&
        ![
          PaymentStatus?.N_A,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup
        .number("Obligatorio")
        .min(1, "Ingrese almenos un metodo de pago")
        .max(5, "Ha superado el limite de métodos  de pago")
        .required("Obligatorio"),
    }),
    method0: yup.string().when(["pvp", "paymentStatus"], {
      is: (pvp, paymentStatus) =>
        pvp > 0 &&
        ![
          PaymentStatus?.N_A,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup.string().required("Obligatorio"),
    }),
    reference0: yup.string().when(["pvp", "paymentStatus"], {
      is: (pvp, paymentStatus) =>
        pvp > 0 &&
        ![
          PaymentStatus?.N_A,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup.string().required("Obligatorio"),
    }),
    amount0: yup.string().when(["pvp", "paymentStatus"], {
      is: (pvp, paymentStatus) =>
        pvp > 0 &&
        ![
          PaymentStatus?.N_A,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup.string().required("Obligatorio"),
    }),
    amountBsS0: yup.string().when(["pvp", "paymentStatus"], {
      is: (pvp, paymentStatus) =>
        pvp > 0 &&
        ![
          PaymentStatus?.N_A,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup.string().required("Obligatorio"),
    }),
  });
};

export const createFollowUpSchema = (t) => {
  return yup.object().shape({});
};

export const CreatePaymentSchema = (t) => {
  return yup.object().shape({
    ...locksmithPaymentFields,
    paymentDifference: yup.number(),
    accumulatedAmount: yup.number(),
    price: servicePriceField,
    pvp: yup.number().transform(emptyToUndefined).when("paymentStatus", {
      is: (paymentStatus) =>
        [PaymentStatus?.DE_CONTADO, PaymentStatus?.CUENTA_POR_COBRAR]?.includes(
          paymentStatus
        ),
      then: yup
        .number("")
        .min(0, "Ingrese un monto valido")
        .required("Obligatorio"),
    }),
    amountHandling: yup.number("").optional(),
    amountExcess: yup.number("").optional(),
    ivaAmount: yup.number("").optional(),
    paymentDate: yup.string().optional(""),
    paymentMethods: yup
      .number()
      .when(["pvp", "paymentStatus", "paymentDifference"], {
        is: (pvp, paymentStatus, paymentDifference) =>
          pvp > 0 &&
          paymentDifference > 0 &&
          ![PaymentStatus?.N_A, PaymentStatus?.CUENTA_POR_COBRAR]?.includes(
            paymentStatus
          ),
        then: yup
          .number("Obligatorio")
          .min(1, "Ingrese almenos un metodo de pago")
          .max(5, "Ha superado el limite de métodos  de pago")
          .required("Obligatorio"),
      }),
    method0: yup.string().when(["pvp", "paymentStatus", "paymentDifference"], {
      is: (pvp, paymentStatus, paymentDifference) =>
        pvp > 0 &&
        paymentDifference > 0 &&
        ![PaymentStatus?.N_A, PaymentStatus?.CUENTA_POR_COBRAR]?.includes(
          paymentStatus
        ),
      then: yup.string().required("Obligatorio"),
    }),
    reference0: yup
      .string()
      .when(
        ["pvp", "paymentStatus", "paymentDifference", "paymentDifference"],
        {
          is: (pvp, paymentStatus, paymentDifference) =>
            pvp > 0 &&
            paymentDifference > 0 &&
            ![PaymentStatus?.N_A, PaymentStatus?.CUENTA_POR_COBRAR]?.includes(
              paymentStatus
            ),
          then: yup.string().required("Obligatorio"),
        }
      ),
    amount0: yup.string().when(["pvp", "paymentStatus", "paymentDifference"], {
      is: (pvp, paymentStatus, paymentDifference) =>
        pvp > 0 &&
        paymentDifference > 0 &&
        ![PaymentStatus?.N_A, PaymentStatus?.CUENTA_POR_COBRAR]?.includes(
          paymentStatus
        ),
      then: yup.string().required("Obligatorio"),
    }),
    amountBsS0: yup
      .string()
      .when(["pvp", "paymentStatus", "paymentDifference"], {
        is: (pvp, paymentStatus, paymentDifference) =>
          pvp > 0 &&
          paymentDifference > 0 &&
          ![PaymentStatus?.N_A, PaymentStatus?.CUENTA_POR_COBRAR]?.includes(
            paymentStatus
          ),
        then: yup.string().required("Obligatorio"),
      }),
  });
};

export const editCashbackSchema = (t) => {
  return yup.object().shape({
    method: yup.string().required("Obligatorio"),
    amount: yup.number().required("Obligatorio"),
    amountBs: yup.number().required("Obligatorio"),
    date: yup.string().required("Obligatorio"),
    ref: yup.string().required("Obligatorio"),
  });
};

export const changeTowSchema = () => {
  return yup.object().shape({
    towType: yup.string().required("Obligatorio"),
    code: yup.string().optional("Obligatorio"),
    approvedBy: yup.string().optional("Obligatorio"),
    externalTowReason: yup.string().optional("Obligatorio"),
    validCode: yup.boolean().optional(""),
  });
};

export const editPaymentSchema = () => {
  return yup.object().shape({
    amount: yup.string().required("Obligatorio"),
    amountBsS: yup.string().optional("Obligatorio"),
    method: yup.string().optional("Obligatorio"),
    reference: yup.string().optional("Obligatorio"),
  });
};

export const allianceSchema = (t) => {
  return yup.object().shape({
    isNew: yup.boolean().required("Obligatorio"),
    enterprise: yup.string().when("isNew", {
      is: (isNew) => isNew !== true,
      then: yup.string("").required("Obligatorio"),
    }),
    newEnterprise: yup.string().when("isNew", {
      is: (isNew) => isNew === true,
      then: yup.string("").required("Obligatorio"),
    }),
    firstName: yup.string().required("Obligatorio"),
    lastName: yup.string().required("Obligatorio"),
    freeAvService: yup.boolean().optional(),
    identificationType: yup.string().required("Obligatorio"),
    identification: yup.number().required("Obligatorio"),
    status: yup.string().required("Obligatorio"),
  });
};

export const ScheduleServiceSchema = (t) => {
  return yup.object().shape({
    date: yup.string().required("Obligatorio"),
    time: yup.string().required("Obligatorio"),
  });
};

export const ScheduleTowSchema = (t) => {
  return yup.object().shape({
    date: yup.string().required("Obligatorio"),
    time: yup.string().required("Obligatorio"),
    reason: yup.string().required("Obligatorio"),
  });
};

export const OTPSchema = (t) => {
  return yup.object().shape({
    code: yup.string().required("Obligatorio"),
  });
};

export const RideryTowOtpSchema = (t) => {
  return yup.object().shape({
    authorizedBy: yup.string().required("Obligatorio"),
    code: yup.string().required("Obligatorio"),
  });
};

export const payTowServiceModal = (t) => {
  return yup.object().shape({
    provider: yup.string().required("Obligatorio"),
    contact: yup.string(),
    vehicleYear: yup.string(),
    vehicleBrand: yup.string(),
    vehicleModel: yup.string(),
    vehiclePlate: yup.string(),
    vehicleColor: yup.string(),
    exceptionReason: yup.string().required("Obligatorio"),
    paymentStatus: yup.string().required("Obligatorio"),
    price: yup.number().required("Obligatorio"),
    // Inputs que se desmontan dejan "" registrado; vacio = sin valor, no NaN
    paid: yup
      .number()
      .transform((value, original) => (original === "" ? undefined : value))
      .optional(),
    paidBs: yup
      .number()
      .transform((value, original) => (original === "" ? undefined : value))
      .optional(),
    isInternal: yup.string().required("Obligatorio"),
    externalTowReason: yup.string().when("isInternal", {
      is: (isInternal) => [userType?.EXTERNO]?.includes(isInternal),
      then: yup.string("").required("Obligatorio"),
    }),
    authorizedBy: yup.string().when("paymentStatus", {
      is: (paymentStatus) => [PaymentStatus?.COURTESY]?.includes(paymentStatus),
      then: yup.string("").required("Obligatorio"),
    }),
    pvp: yup.number().when("paymentStatus", {
      is: (paymentStatus) =>
        [PaymentStatus?.DE_CONTADO, PaymentStatus?.CUENTA_POR_COBRAR]?.includes(
          paymentStatus
        ),
      then: yup
        .number("")
        .min(0, "Ingrese un monto valido")
        .required("Obligatorio"),
    }),
    amountHandling: yup.number().when("paymentStatus", {
      is: (paymentStatus) =>
        [PaymentStatus?.DE_CONTADO, PaymentStatus?.CUENTA_POR_COBRAR]?.includes(
          paymentStatus
        ),
      then: yup
        .number("")
        .min(0, "Ingrese un monto valido")
        .required("Obligatorio"),
    }),
    amountExcess: yup.number().when("paymentStatus", {
      is: (paymentStatus) =>
        [PaymentStatus?.DE_CONTADO, PaymentStatus?.CUENTA_POR_COBRAR]?.includes(
          paymentStatus
        ),
      then: yup
        .number("")
        .min(0, "Ingrese un monto valido")
        .required("Obligatorio"),
    }),
    paymentMethods: yup.number().when(["pvp", "paymentStatus"], {
      is: (pvp, paymentStatus) =>
        pvp > 0 &&
        ![
          PaymentStatus?.N_A,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup
        .number("Obligatorio")
        .min(1, "Ingrese almenos un metodo de pago")
        .max(5, "Ha superado el limite de métodos  de pago")
        .required("Obligatorio"),
    }),
    method0: yup.string().when(["pvp", "paymentStatus"], {
      is: (pvp, paymentStatus) =>
        pvp > 0 &&
        ![
          PaymentStatus?.N_A,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup.string().required("Obligatorio"),
    }),
    reference0: yup.string().when(["pvp", "paymentStatus"], {
      is: (pvp, paymentStatus) =>
        pvp > 0 &&
        ![
          PaymentStatus?.N_A,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup.string().required("Obligatorio"),
    }),
    amount0: yup.string().when(["pvp", "paymentStatus"], {
      is: (pvp, paymentStatus) =>
        pvp > 0 &&
        ![
          PaymentStatus?.N_A,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup.string().required("Obligatorio"),
    }),
    amountBsS0: yup.string().when(["pvp", "paymentStatus"], {
      is: (pvp, paymentStatus) =>
        pvp > 0 &&
        ![
          PaymentStatus?.N_A,
          PaymentStatus?.CUENTA_POR_COBRAR,
          PaymentStatus?.COURTESY,
        ]?.includes(paymentStatus),
      then: yup.string().required("Obligatorio"),
    }),
  });
};

export const CreateParticularCustomer = (t) => {
  return yup.object().shape({
    firstName: yup.string().required("Obligatorio"),
    lastName: yup.string().required("Obligatorio"),
    phone: yup.string().required("Obligatorio"),
    vehicleBrand: yup.string().required("Obligatorio"),
    vehicleModel: yup.string().required("Obligatorio"),
    vehicleYear: yup
      .number()
      .min(1940, "El año del vehiculo debe ser mayor a 1940")
      .max(new Date().getFullYear() + 1, "Ingrese un año valido")
      .required("Obligatorio"),
    vehicleColor: yup.string().required("Obligatorio"),
    vehiclePlate: yup.string().required("Obligatorio"),
  });
};

export const NewInternalProviderSchema = (t) => {
  return yup.object().shape({
    contactName: yup.string().required("Obligatorio"),
    identification: yup.string().required("Obligatorio"),
    identificationType: yup.string().required("Obligatorio"),
    phonePrefix: yup.string().required("Obligatorio"),
    phoneNumber: yup
      .string()
      .min(7, "Ingrese un telefono valido")
      .max(7, "Ingrese un telefono valido")
      .required("Obligatorio"),
    type: yup.string().required("Obligatorio"),
    state: yup.string().required("Obligatorio"),
    isInternal: yup.boolean().required("Obligatorio"),
    isActive: yup.boolean().required("Obligatorio"),
  });
};

export const RegisterSchema = (t) => {
  return yup.object().shape({
    firstName: yup.string().required("Obligatiorio"),
    lastName: yup.string().required("Obligatiorio"),
    email: yup
      .string()
      .required(t("schema.required", { label: t("form.labels.email") }))
      .email(t("schema.invalid", { label: t("form.labels.email") })),
    password: yup
      .string()
      .min(
        3,
        t("schema.min_length", {
          label: t("form.labels.password"),
          characters: 3,
        })
      )
      .max(
        50,
        t("schema.max_length", {
          label: t("form.labels.password"),
          characters: 50,
        })
      )
      .required(t("schema.required", { label: t("from.labels.password") })),
    confirmPassword: yup.string().oneOf([yup.ref("password"), "as"]),
  });
};

export const CreateEmployeeSchema = (t) => {
  return yup.object().shape({
    firstName: yup.string().required("Obligatiorio"),
    lastName: yup.string().required("Obligatiorio"),
    email: yup
      .string()
      .required(t("Obligatorio", { label: "Obligatorio" }))
      .email("Formato invalido"),
    phone: yup.string().required("Obligatorio"),
    baseSalary: yup.number().required("Obligatorio"),
    cryptoAddress: yup.string().required("Obligatorio"),
  });
};

export const RegisterStepTwoSchema = (t) => {
  return yup.object().shape({
    companyName: yup.string().required("Obligatorio"),
    companySector: yup.string().required("Obligatorio"),
  });
};

export const revisionVehicleSchema = (t) => {
  return yup.object().shape({
    code: yup.string().optional(""),
    plate: yup.string().optional(""),
    year: yup.string().optional(""),
    model: yup.string().optional(""),
    brand: yup.string().optional(""),
    color: yup.string().optional(""),
    phone: yup.string().optional(""),
    particularCustomer: yup.string().optional(""),
    // Required fields
    coolantLevel: yup.string().required("Obligatorio"),
    engineOilLevel: yup.string().required("Obligatorio"),
    brakeFluidLevel: yup.string().required("Obligatorio"),
    gearboxOilLevel: yup.string().required("Obligatorio"),
    powerSteeringFluidLevel: yup.string().required("Obligatorio"),
    // Required fields
    engineOilCondition: yup.string().required("Obligatorio"),
    coolantFluidCondition: yup.string().required("Obligatorio"),
    powerSteeringFluidCondition: yup.string().required("Obligatorio"),
    brakeFluidCondition: yup.string().required("Obligatorio"),
    gearboxOilCondition: yup.string().required("Obligatorio"),
    timingBeltCondition: yup.string().required("Obligatorio"),
    uniqueBeltCondition: yup.string().required("Obligatorio"),
    alternatorBeltCondition: yup.string().required("Obligatorio"),
    airConditionerBeltCondition: yup.string().required("Obligatorio"),
    steeringPumpBeltCondition: yup.string().required("Obligatorio"),
    pulleysCondition: yup.string().required("Obligatorio"),
    tensorCondition: yup.string().required("Obligatorio"),
    upperHosesCondition: yup.string().required("Obligatorio"),
    lowerHosesCondition: yup.string().required("Obligatorio"),
    thermostatCondition: yup.string().required("Obligatorio"),
    coolantTankCondition: yup.string().required("Obligatorio"),
    // required fields
    valveCoverLeak: yup.string().required("Obligatorio"),
    oilPanLeak: yup.string().required("Obligatorio"),
    gearboxPanLeak: yup.string().required("Obligatorio"),
    frontcrankshaftSealLeak: yup.string().required("Obligatorio"),
    rearCrankshaftSealLeak: yup.string().required("Obligatorio"),
    camshaftSealLeak: yup.string().required("Obligatorio"),
    gearboxSealLeak: yup.string().required("Obligatorio"),
    steeringHosesLeak: yup.string().required("Obligatorio"),
    steeringPumpLeak: yup.string().required("Obligatorio"),
    transferCaseLeak: yup.string().required("Obligatorio"),
    differentialLeak: yup.string().required("Obligatorio"),
    UpperRadiatorTankLeak: yup.string().required("Obligatorio"),
    LowerRadiatorTankLeak: yup.string().required("Obligatorio"),
    // required fields
    serviceDate: yup.string().required("Obligatorio"),
    fuelPumpHealth: yup.string().required("Obligatorio"),
  });
};
export const fleetMaintenanceSchema = () => {
  return yup.object().shape({
    type: yup.string().required("Obligatorio"),
    createdBy: yup.string().required("Obligatorio"),
    unit: yup.string().required("Obligatorio"),
    approvedBy: yup.string().required("Obligatorio"),
    creationDate: yup.string().required("Obligatorio"),
    systemId: yup.string().required("Obligatorio"),
    componentId: yup.string().required("Obligatorio"),
    cost: yup.number().required("Obligatorio"),
    provider: yup.string().required("Obligatorio"),
    dateIn: yup.date().required("Obligatorio"),
    dateOut: yup.date().nullable(),
    km: yup.number().required("Obligatorio"),
    unitStatus: yup.string().required("Obligatorio"),
    comment: yup.string(),
    maintenanceType: yup.string().required("Obligatorio"),
  });
};
export const editFleetMaintenanceSchema = () => {
  return yup.object().shape({
    type: yup.string(),
    createdBy: yup.string(),
    unit: yup.string(),
    approvedBy: yup.string(),
    creationDate: yup.string(),
    systemId: yup.string(),
    componentId: yup.string(),
    cost: yup.number(),
    provider: yup.string(),
    dateIn: yup.string(),
    dateOut: yup.string(),
    km: yup.number(),
    unitStatus: yup.string(),
    comment: yup.string(),
    maintenanceType: yup.string(),
  });
};

export const fleetIncidenceSchema = () => {
  return yup.object().shape({
    unitType: yup.string().required("Obligatorio"),
    unit: yup.string().required("Obligatorio"),
    registeredBy: yup.string().required("Obligatorio"),
    registrationDate: yup.string().required("Obligatorio"),
    accidentDate: yup.string().required("Obligatorio"),
    driver: yup.string().required("Obligatorio"),
    accidentLocation: yup.string().required("Obligatorio"),
    unitStatus: yup.string().required("Obligatorio"),
    detail: yup.string(),
  });
};

export const editFleetIncidenceSchema = () => {
  return yup.object().shape({
    unitType: yup.string(),
    unit: yup.string(),
    registeredBy: yup.string(),
    registrationDate: yup.string(),
    accidentDate: yup.string(),
    driver: yup.string(),
    accidentLocation: yup.string(),
    unitStatus: yup.string(),
    detail: yup.string(),
  });
};

export const dailyReportSchema = () => {
  return yup.object().shape({
    hourTo: yup.string().required("Obligatorio"),
    hourFrom: yup.string().required("Obligatorio"),
    errorCommited: yup.number().required("Obligatorio"),
  });
};

export const createComplaintSchema = () => {
  return yup.object().shape({
    complaintDate: yup.string().required("Obligatorio"),
    classification: yup.string().required("Obligatorio"),
    details: yup.string().required("Obligatorio"),
    status: yup.string().required("Obligatorio"),
    socialMedia: yup.string().optional(""),
    plate: yup.string().optional(""),
    category: yup.string().optional(""),
    serviceId: yup.string().optional(""),
    clientName: yup.string().required("Obligatorio"),
    clientPhone: yup.string().required("Obligatorio"),
    //when status is "Finalizado" all except solution amount is required
    solution: yup.string().when("status", {
      is: (status) => status === "Finalizado",
      then: yup.string().required("Obligatorio"),
    }),
    solutionDetails: yup.string().when(["status", "solution"], {
      is: (status, solution) => status === "Finalizado" && solution,
      then: yup.string().required("Obligatorio"),
    }),
    clientSatisfaction: yup.string().when("status", {
      is: (status) => status === "Finalizado",
      then: yup.string().required("Obligatorio"),
    }),
    segmentation: yup.string().required("Obligatorio"),
  });
};

export const editComplaintSchema = () => {
  return yup.object().shape({
    complaintDate: yup.string(),
    classification: yup.string(),
    details: yup.string(),
    socialMedia: yup.string().optional(""),
    plate: yup.string().optional(""),
    category: yup.string().optional(""),
    status: yup.string(),
    clientName: yup.string(),
    clientPhone: yup.string(),
    serviceId: yup.string(),
    //when status is "Finalizado" all except solution amount is required
    solution: yup.string().when("status", {
      is: (status) => status === "Finalizado",
      then: yup.string().required("Obligatorio"),
    }),
    solutionDetails: yup.string().when(["status", "solution"], {
      is: (status, solution) => status === "Finalizado" && solution,
      then: yup.string().required("Obligatorio"),
    }),
    clientSatisfaction: yup.string().when("status", {
      is: (status) => status === "Finalizado",
      then: yup.string().required("Obligatorio"),
    }),
    leadContributor: yup.string().optional(""),
    contributorName: yup.string().optional(""),
    segmentation: yup.string().required("Obligatorio"),
  });
};

const isMapsTimeType = (timeType) =>
  timeType != null && /map|google/i.test(String(timeType));

const alarmTimeItemNumericField = (field) =>
  yup.mixed().test(`alarm-${field}`, "Obligatorio", function (value) {
    if (isMapsTimeType(this.parent.timeType)) return true;
    if (value === "" || value == null)
      return this.createError({ message: "Obligatorio" });
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0 || n > 999)
      return this.createError({ message: "Ingrese 0 a 999" });
    return true;
  });

const alarmTimeLimitCriticTest = (schema, name, message, pickPair) =>
  schema.test(name, message, function (value) {
    if (isMapsTimeType(this.parent.timeType)) return true;
    const { limit, critic } = pickPair(this.parent, value);
    if (!Number.isFinite(limit) || !Number.isFinite(critic)) return true;
    return limit <= critic;
  });

const alarmTimeLimitTimeField = () =>
  alarmTimeLimitCriticTest(
    alarmTimeItemNumericField("limitTime"),
    "limitLteCritic",
    "El tiempo límite no puede ser mayor que el tiempo crítico",
    (p, v) => ({ limit: Number(v), critic: Number(p.criticTime) })
  );

const alarmTimeCriticTimeField = () =>
  alarmTimeLimitCriticTest(
    alarmTimeItemNumericField("criticTime"),
    "criticGteLimit",
    "El tiempo crítico no puede ser menor que el tiempo límite",
    (p, v) => ({ limit: Number(p.limitTime), critic: Number(v) })
  );

export const alarmTimeParamsFormSchema = () => {
  return yup.object().shape({
    items: yup
      .array()
      .of(
        yup.object().shape({
          _id: yup.mixed().nullable(),
          stageId: yup.mixed().nullable(),
          stage: yup.mixed().nullable(),
          isActive: yup.mixed().nullable(),
          order: yup.mixed().nullable(),
          color: yup.mixed().nullable(),
          timeType: yup.string().nullable(),
          limitTime: alarmTimeLimitTimeField(),
          criticTime: alarmTimeCriticTimeField(),
        })
      )
      .min(0),
  });
};

const servScalePct = (label) =>
  yup
    .number()
    .typeError("Valor numérico")
    .min(0, `${label}: mínimo 0`)
    .max(100, `${label}: máximo 100`)
    .required("Obligatorio");

const servScaleMoney = (label) =>
  yup
    .number()
    .typeError("Valor numérico")
    .min(0, `${label}: mínimo 0`)
    .required("Obligatorio");

export const servScalePriceFormSchema = () => {
  return yup.object().shape({
    priceHitch: servScaleMoney("Precio de enganche"),
    priceKm: servScaleMoney("Monto por kilómetro"),
    nocturnalSurchargePct: servScalePct("% Recargo nocturno"),
    minPriceDayInt: servScaleMoney("Monto mín. diurno (interno)"),
    minPriceDayExt: servScaleMoney("Monto mín. diurno (externo)"),
    minPriceNightInt: servScaleMoney("Monto mín. nocturno (interno)"),
    minPriceNightExt: servScaleMoney("Monto mín. nocturno (externo)"),
    intTowPricePct: servScalePct("% gruero interno"),
    extTowPricePct: servScalePct("% gruero externo (distribución)"),
    extTowEarnPct: servScalePct("Ganancia gruero externo"),
    intTowEarnPct: servScalePct("Ganancia gruero interno"),
    avEarnPct: servScalePct("Ganancia asistente vial"),
    dateSince: yup
      .string()
      .required("Obligatorio")
      .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato AAAA-MM-DD (ej. 2026-04-02)"),
  });
};

const commissionMoney = (label) =>
  yup
    .number()
    .typeError("Valor numérico")
    .min(0, `${label}: mínimo 0`)
    .required("Obligatorio");

export const operationsCommissionFormSchema = () => {
  return yup.object().shape({
    battery: commissionMoney("Venta de batería"),
    roleCoord: commissionMoney("Coordinador"),
    roleOperator: commissionMoney("Operador"),
    roleOperatorExtraSince: commissionMoney("Extra operador (umbral)"),
    roleOperatorExtraAmount: commissionMoney("Extra operador (monto)"),
    roleSuperv: commissionMoney("Supervisor"),
    solvedByAv: commissionMoney("Avería resuelta"),
    dateSince: yup
      .string()
      .required("Obligatorio")
      .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato AAAA-MM-DD (ej. 2026-04-02)"),
  });
};

const refundReceiptTypes = ["image/jpeg", "image/png"];

export const locksmithRefundSchema = yup.object().shape({
  refundDate: yup
    .date()
    .typeError("Obligatorio")
    .required("Obligatorio")
    .test("notFuture", "La fecha no puede ser futura", (date) =>
      date ? date <= new Date() : true
    ),
  reference: yup
    .string()
    .trim()
    .matches(/^\d+$/, "Solo números")
    .required("Obligatorio"),
  receiptImg: yup
    .mixed()
    .required("Obligatorio")
    .test("fileType", "Formatos permitidos: JPG, PNG", (file) =>
      file ? refundReceiptTypes.includes(file.type) : false
    ),
});
